import { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';

const ProductoModal = ({ isOpen, onClose, onSave, initialValues, loading }) => {
  // Estado del formulario (texto, checkboxes)
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    precio: '',
    oferta: 0,
    activo: 1,
  });

  // Archivos nuevos seleccionados (File objects)
  const [imagenesNuevas, setImagenesNuevas] = useState({
    imagen1: null,
    imagen2: null,
    imagen3: null,
  });

  // URLs de imágenes existentes (para mostrar preview)
  const [imagenesExistentes, setImagenesExistentes] = useState({
    imagen1: '',
    imagen2: '',
    imagen3: '',
  });

  // Indica si se debe eliminar una imagen existente
  const [eliminarImagenes, setEliminarImagenes] = useState({
    imagen1: false,
    imagen2: false,
    imagen3: false,
  });

  // Resetear estado cuando se abre/cierra el modal o cambia initialValues
  useEffect(() => {
    if (initialValues) {
      setForm({
        titulo: initialValues.titulo || '',
        descripcion: initialValues.descripcion || '',
        precio: initialValues.precio || '',
        oferta: initialValues.oferta || 0,
        activo: initialValues.activo ?? 1,
      });
      setImagenesExistentes({
        imagen1: initialValues.imagen1 || '',
        imagen2: initialValues.imagen2 || '',
        imagen3: initialValues.imagen3 || '',
      });
      // No marcar eliminación al abrir
      setEliminarImagenes({ imagen1: false, imagen2: false, imagen3: false });
    } else {
      setForm({ titulo: '', descripcion: '', precio: '', oferta: 0, activo: 1 });
      setImagenesExistentes({ imagen1: '', imagen2: '', imagen3: '' });
      setEliminarImagenes({ imagen1: false, imagen2: false, imagen3: false });
    }
    // Limpiar archivos nuevos al abrir
    setImagenesNuevas({ imagen1: null, imagen2: null, imagen3: null });
  }, [initialValues, isOpen]);

  // Manejar cambios en inputs de texto y checkboxes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  // Manejar selección de archivo
  const handleFileChange = (key, file) => {
    if (file) {
      // Si se selecciona un archivo, se elimina la imagen existente (se reemplaza)
      setImagenesNuevas((prev) => ({ ...prev, [key]: file }));
      // Si se había marcado para eliminar, se desmarca porque ahora hay nuevo archivo
      setEliminarImagenes((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Quitar archivo nuevo seleccionado (vuelve a mostrar la imagen existente, si la hay)
  const handleRemoveNewImage = (key) => {
    setImagenesNuevas((prev) => ({ ...prev, [key]: null }));
  };

  // Marcar una imagen existente para ser eliminada
  const handleDeleteExistingImage = (key) => {
    if (imagenesExistentes[key]) {
      setEliminarImagenes((prev) => ({ ...prev, [key]: true }));
      // Si había un archivo nuevo para esta clave, lo descartamos
      setImagenesNuevas((prev) => ({ ...prev, [key]: null }));
    }
  };

  // Obtener la URL de previsualización (nueva imagen o existente)
  const getImagePreview = (key) => {
    if (imagenesNuevas[key]) {
      return URL.createObjectURL(imagenesNuevas[key]);
    }
    // Si está marcada para eliminar, no mostrar preview
    if (eliminarImagenes[key]) {
      return null;
    }
    return imagenesExistentes[key] || null;
  };

  // Enviar formulario
  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();

    // Campos de texto
    formData.append('titulo', form.titulo);
    formData.append('descripcion', form.descripcion);
    formData.append('precio', parseFloat(form.precio) || 0);
    formData.append('oferta', form.oferta);
    formData.append('activo', form.activo);

    // Archivos nuevos (si los hay)
    Object.keys(imagenesNuevas).forEach((key) => {
      if (imagenesNuevas[key]) {
        formData.append(key, imagenesNuevas[key]);
      }
    });

    // Indicadores de eliminación para imágenes existentes
    Object.keys(eliminarImagenes).forEach((key) => {
      if (eliminarImagenes[key]) {
        formData.append(`eliminar_${key}`, '1');
      }
    });

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="sp-modal-overlay" onClick={onClose}>
      <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sp-modal-header">
          <h2>{initialValues ? 'Editar producto' : 'Crear producto'}</h2>
          <button className="sp-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="sp-modal-body">
            {/* Título */}
            <div className="sp-form-group">
              <label htmlFor="titulo">Título *</label>
              <input
                id="titulo"
                name="titulo"
                type="text"
                value={form.titulo}
                onChange={handleChange}
                required
                className="sp-input"
                placeholder="Ej: Huesito pequeño"
              />
            </div>

            {/* Descripción */}
            <div className="sp-form-group">
              <label htmlFor="descripcion">Descripción</label>
              <textarea
                id="descripcion"
                name="descripcion"
                rows="3"
                value={form.descripcion}
                onChange={handleChange}
                className="sp-input"
                placeholder="Detalles del producto"
              />
            </div>

            {/* Precio + Checkboxes en fila */}
            <div className="sp-form-row">
              <div className="sp-form-group" style={{ flex: 1.5 }}>
                <label htmlFor="precio">Precio (ARS)</label>
                <input
                  id="precio"
                  name="precio"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.precio}
                  onChange={handleChange}
                  className="sp-input"
                  placeholder="0.00"
                />
              </div>
              <div className="sp-form-group" style={{ flex: 1 }}>
                <label>En oferta</label>
                <div className="sp-checkbox-wrapper">
                  <input
                    id="oferta"
                    name="oferta"
                    type="checkbox"
                    checked={form.oferta === 1}
                    onChange={handleChange}
                    className="sp-checkbox"
                  />
                  <label htmlFor="oferta" className="sp-checkbox-label">
                    {form.oferta ? 'Sí' : 'No'}
                  </label>
                </div>
              </div>
              <div className="sp-form-group" style={{ flex: 1 }}>
                <label>Activo</label>
                <div className="sp-checkbox-wrapper">
                  <input
                    id="activo"
                    name="activo"
                    type="checkbox"
                    checked={form.activo === 1}
                    onChange={handleChange}
                    className="sp-checkbox"
                  />
                  <label htmlFor="activo" className="sp-checkbox-label">
                    {form.activo ? 'Sí' : 'No'}
                  </label>
                </div>
              </div>
            </div>

            {/* Imágenes */}
            <div className="sp-images-section">
              <h4>Imágenes del producto</h4>
              <div className="sp-images-grid">
                {['imagen1', 'imagen2', 'imagen3'].map((key) => {
                  const previewUrl = getImagePreview(key);
                  const hasNew = !!imagenesNuevas[key];
                  const hasExisting = !!imagenesExistentes[key] && !eliminarImagenes[key];
                  const isMarkedForDelete = eliminarImagenes[key];

                  return (
                    <div key={key} className="sp-image-box">
                      <div className="sp-image-preview">
                        {previewUrl ? (
                          <img src={previewUrl} alt={`Vista previa ${key}`} />
                        ) : (
                          <ImageIcon size={32} className="sp-image-placeholder" />
                        )}
                      </div>
                      <div className="sp-image-actions">
                        {!isMarkedForDelete && (
                          <>
                            <label htmlFor={`file-${key}`} className="sp-btn sp-btn--light sp-btn--small">
                              <Upload size={14} />
                              {hasExisting ? 'Cambiar' : 'Subir'}
                            </label>
                            <input
                              id={`file-${key}`}
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleFileChange(key, file);
                                e.target.value = '';
                              }}
                            />
                          </>
                        )}
                        {hasNew && (
                          <button
                            type="button"
                            className="sp-btn sp-btn--danger sp-btn--small"
                            onClick={() => handleRemoveNewImage(key)}
                          >
                            Quitar
                          </button>
                        )}
                        {hasExisting && !hasNew && (
                          <>
                            <span className="sp-image-status">Actual</span>
                            <button
                              type="button"
                              className="sp-btn sp-btn--danger sp-btn--small"
                              onClick={() => handleDeleteExistingImage(key)}
                              title="Eliminar esta imagen"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                        {isMarkedForDelete && (
                          <span className="sp-image-status" style={{ color: '#ef4444' }}>
                            Se eliminará
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="sp-image-hint">
                {initialValues
                  ? 'Seleccioná una nueva imagen para reemplazar la existente, o elimínala con el botón 🗑️.'
                  : 'Subí hasta 3 imágenes para el producto.'}
              </p>
            </div>
          </div>

          <div className="sp-modal-footer">
            <button type="button" className="sp-btn sp-btn--light" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={loading}>
              {loading && <Loader2 size={16} className="sp-spinner" />}
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>

        {/* Estilos (igual que antes) */}
        <style jsx>{`
          .sp-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
            animation: fadeIn 0.25s ease;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.96); }
            to { opacity: 1; transform: scale(1); }
          }
          .sp-modal {
            background: #ffffff;
            border-radius: 16px;
            max-width: 700px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
            display: flex;
            flex-direction: column;
          }
          .sp-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem 1.5rem;
            border-bottom: 1px solid #eef2f6;
          }
          .sp-modal-header h2 {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 700;
            color: #1a1a2e;
          }
          .sp-modal-close {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0.25rem;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7280;
            transition: background 0.2s, color 0.2s;
          }
          .sp-modal-close:hover {
            background: #f3f4f6;
            color: #1a1a2e;
          }
          .sp-modal-body {
            padding: 1.5rem;
            flex: 1;
            overflow-y: auto;
          }
          .sp-modal-footer {
            padding: 1rem 1.5rem;
            border-top: 1px solid #eef2f6;
            display: flex;
            justify-content: flex-end;
            gap: 0.75rem;
            background: #fafbfc;
            border-radius: 0 0 16px 16px;
          }
          .sp-form-group {
            margin-bottom: 1.25rem;
          }
          .sp-form-group label {
            display: block;
            font-weight: 600;
            font-size: 0.875rem;
            color: #374151;
            margin-bottom: 0.3rem;
          }
          .sp-input {
            width: 100%;
            padding: 0.6rem 0.8rem;
            border: 1.5px solid #e5e7eb;
            border-radius: 8px;
            font-size: 0.95rem;
            transition: border-color 0.2s, box-shadow 0.2s;
            background: #ffffff;
            color: #1a1a2e;
          }
          .sp-input:focus {
            outline: none;
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
          }
          .sp-input::placeholder {
            color: #9ca3af;
          }
          textarea.sp-input {
            resize: vertical;
            min-height: 70px;
          }
          .sp-form-row {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
          }
          .sp-checkbox-wrapper {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            margin-top: 0.2rem;
          }
          .sp-checkbox {
            width: 1.1rem;
            height: 1.1rem;
            accent-color: #6366f1;
            cursor: pointer;
          }
          .sp-checkbox-label {
            font-size: 0.9rem;
            font-weight: 500;
            color: #4b5563;
            cursor: pointer;
            margin: 0;
          }
          .sp-images-section {
            margin-top: 1.5rem;
            border-top: 1px solid #eef2f6;
            padding-top: 1.25rem;
          }
          .sp-images-section h4 {
            margin: 0 0 0.75rem 0;
            font-size: 1rem;
            font-weight: 700;
            color: #1a1a2e;
          }
          .sp-images-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
            gap: 1rem;
          }
          .sp-image-box {
            border: 1.5px dashed #d1d5db;
            border-radius: 12px;
            padding: 0.5rem;
            background: #f9fafb;
            transition: border-color 0.2s, background 0.2s;
          }
          .sp-image-box:hover {
            border-color: #a5b4fc;
            background: #f5f3ff;
          }
          .sp-image-preview {
            min-height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            border-radius: 8px;
            overflow: hidden;
          }
          .sp-image-preview img {
            max-width: 100%;
            max-height: 120px;
            object-fit: contain;
          }
          .sp-image-placeholder {
            color: #d1d5db;
          }
          .sp-image-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.3rem;
            margin-top: 0.5rem;
            justify-content: center;
          }
          .sp-image-status {
            font-size: 0.7rem;
            color: #6b7280;
            background: #f3f4f6;
            padding: 0.15rem 0.5rem;
            border-radius: 999px;
            align-self: center;
          }
          .sp-image-hint {
            margin: 0.5rem 0 0;
            font-size: 0.75rem;
            color: #6b7280;
          }
          .sp-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.4rem;
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            font-size: 0.9rem;
            cursor: pointer;
            transition: all 0.2s;
            text-decoration: none;
          }
          .sp-btn--small {
            padding: 0.25rem 0.6rem;
            font-size: 0.75rem;
            border-radius: 6px;
          }
          .sp-btn--primary {
            background: #6366f1;
            color: #ffffff;
          }
          .sp-btn--primary:hover:not(:disabled) {
            background: #4f46e5;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          }
          .sp-btn--light {
            background: #f3f4f6;
            color: #374151;
          }
          .sp-btn--light:hover:not(:disabled) {
            background: #e5e7eb;
          }
          .sp-btn--danger {
            background: #ef4444;
            color: #ffffff;
          }
          .sp-btn--danger:hover:not(:disabled) {
            background: #dc2626;
          }
          .sp-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .sp-spinner {
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .sp-modal::-webkit-scrollbar {
            width: 6px;
          }
          .sp-modal::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 8px;
          }
          .sp-modal::-webkit-scrollbar-thumb {
            background: #c4b5fd;
            border-radius: 8px;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ProductoModal;